import { Actor, HttpAgent, type Identity } from "@icp-sdk/core/agent";
import { Principal } from "@icp-sdk/core/principal";
import type * as LuckyWheelTypes from "@/lib/idl/lucky_wheel_idl";
import type { _SERVICE } from "@/lib/idl/lucky_wheel_idl";
import { idlFactory } from "@/lib/idl/lucky_wheel_idl.js";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const clientLog = createLogAdapter("lib/clients/LuckyWheelClient.ts");

/**
 * 构建市场订单过滤器的辅助函数
 *
 * @example
 * const filter = buildMarketFilter({
 *   minPrice: 1000,
 *   maxPrice: 5000,
 *   minWinProb: 50,
 *   maxWinProb: 80,
 * });
 */
export function buildMarketFilter(options: {
	status?: LuckyWheelTypes.OrderStatus;
	minPrice?: number | bigint;
	maxPrice?: number | bigint;
	minWinProb?: number | bigint;
	maxWinProb?: number | bigint;
	minLevelParam?: number | bigint;
	maxLevelParam?: number | bigint;
}): LuckyWheelTypes.MarketFilterV2 {
	return {
		status: options.status ? [options.status] : [],
		minPrice: options.minPrice !== undefined ? [BigInt(options.minPrice)] : [],
		maxPrice: options.maxPrice !== undefined ? [BigInt(options.maxPrice)] : [],
		minWinProb:
			options.minWinProb !== undefined ? [BigInt(options.minWinProb)] : [],
		maxWinProb:
			options.maxWinProb !== undefined ? [BigInt(options.maxWinProb)] : [],
		minLevelParam:
			options.minLevelParam !== undefined
				? [BigInt(options.minLevelParam)]
				: [],
		maxLevelParam:
			options.maxLevelParam !== undefined
				? [BigInt(options.maxLevelParam)]
				: [],
	};
}

/**
 * Lucky Wheel canister 客户端，封装所有抽奖相关的操作
 *
 * 版本：v6.5
 * - 采用统一分页架构
 * - 市场订单唯一ID支持
 * - 删除过时方法，使用新的分页查询接口
 * - 支持 MarketFilterV2 区间查询（maxWinProb、maxLevelParam）
 * - v6.5: 新增提现限额查询接口（getUserWithdrawStatus）
 */
export class LuckyWheelClient {
	private actor: _SERVICE;
	private canisterId = "lxipt-3aaaa-aaaap-qqecq-cai"; // Lucky Wheel canister ID

	constructor(identity: Identity | null = null) {
		try {
			// 验证 canister ID 是否有效
			Principal.fromText(this.canisterId);

			// 创建 agent
			const agentOptions = identity ? { identity } : {};

			const agent = new HttpAgent({
				host: process.env.NEXT_PUBLIC_IC_HOST || "https://ic0.app",
				verifyQuerySignatures: false,
				...agentOptions,
			});

			// 开发环境需要获取根密钥
			if (process.env.NODE_ENV === "development") {
				agent.fetchRootKey().catch((err) => {
					clientLog.warn("Failed to fetch root key:", err);
				});
			}

			this.actor = Actor.createActor<_SERVICE>(idlFactory, {
				agent,
				canisterId: this.canisterId,
			});
		} catch (error) {
			clientLog.error("Failed to initialize LuckyWheelClient:", error);
			throw new Error(`Invalid canister ID: ${this.canisterId}`);
		}
	}

	// ================= 余额系统 =================

	/**
	 * 获取用户余额
	 */
	async getUserBalance(user: Principal): Promise<LuckyWheelTypes.UserBalance> {
		try {
			return await this.actor.getUserBalance(user);
		} catch (error) {
			clientLog.error("Error getting user balance:", error);
			throw error;
		}
	}

	/**
	 * 查询余额交易记录（v6.1统一分页接口）
	 */
	async queryTransactions(
		scope: LuckyWheelTypes.TransactionQueryScope,
		cursor: bigint | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 20n,
	): Promise<LuckyWheelTypes.PaginatedResult_2> {
		try {
			return await this.actor.queryTransactions(
				scope,
				cursor ? [cursor] : [],
				direction,
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying transactions:", error);
			throw error;
		}
	}

	/**
	 * 提现余额（v5.1：自动提现到调用者本人账号）
	 */
	async withdraw(amount: bigint): Promise<LuckyWheelTypes.Result_1> {
		try {
			return await this.actor.withdraw(amount);
		} catch (error) {
			clientLog.error("Error withdrawing balance:", error);
			throw error;
		}
	}

	/**
	 * 获取用户提现状态和限额信息（v6.5新增）
	 *
	 * @param user - 用户 Principal
	 * @returns 提现状态，包含：
	 *   - maxPerTx: 单次提现上限（100,000 DD）
	 *   - max24h: 24小时累计提现上限（200,000 DD）
	 *   - maxCount24h: 24小时提现次数上限（10次）
	 *   - used24h: 24小时内已使用额度
	 *   - usedCount24h: 24小时内已提现次数
	 *   - remaining24h: 24小时内剩余额度
	 *   - remainingCount24h: 24小时内剩余次数
	 *   - windowStart: 时间窗口起始时间
	 *   - windowEnd: 时间窗口结束时间
	 *   - lastWithdrawTime: 上次提现时间（可选）
	 */
	async getUserWithdrawStatus(
		user: Principal,
	): Promise<LuckyWheelTypes.WithdrawStatus> {
		try {
			return await this.actor.getUserWithdrawStatus(user);
		} catch (error) {
			clientLog.error("Error getting user withdraw status:", error);
			throw error;
		}
	}

	// ================= 奖券管理 =================

	/**
	 * 使用余额购买奖券（v8.0 支持邀请码）
	 * @param quantity - 购买数量
	 * @param referralCode - 可选的邀请码（8位字符）
	 */
	async buyTicketsWithBalance(
		quantity: bigint,
		referralCode?: string,
	): Promise<string[]> {
		try {
			const result = await this.actor.buyTicketsWithBalance(
				quantity,
				referralCode ? [referralCode] : [],
			);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error buying tickets:", error);
			throw error;
		}
	}

	/**
	 * 验证奖券（分配概率）
	 */
	async verifyTicket(ticketId: string): Promise<LuckyWheelTypes.TicketInfo> {
		try {
			const result = await this.actor.verifyTicket(ticketId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error verifying ticket:", error);
			throw error;
		}
	}

	/**
	 * 批量验证奖券
	 */
	async verifyTicketsBatch(
		ticketIds: string[],
	): Promise<LuckyWheelTypes.TicketInfo[]> {
		try {
			const result = await this.actor.verifyTicketsBatch(ticketIds);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error batch verifying tickets:", error);
			throw error;
		}
	}

	/**
	 * 洗券（提升概率）
	 */
	async refreshTicket(ticketId: string): Promise<LuckyWheelTypes.TicketInfo> {
		try {
			const result = await this.actor.refreshTicket(ticketId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error refreshing ticket:", error);
			throw error;
		}
	}

	/**
	 * 获取用户奖券
	 */
	async getUserTickets(
		user: Principal,
		filter?: LuckyWheelTypes.TicketFilter,
	): Promise<LuckyWheelTypes.TicketInfo[]> {
		try {
			return await this.actor.getUserTickets(user, filter ? [filter] : []);
		} catch (error) {
			clientLog.error("Error getting user tickets:", error);
			throw error;
		}
	}

	/**
	 * 获取用户奖券摘要（前端聚合实现）
	 */
	async getUserTicketSummary(user: Principal): Promise<{
		total: number;
		verified: number;
		unverified: number;
		used: number;
		listed: number;
	}> {
		try {
			const tickets = await this.getUserTickets(user);
			return {
				total: tickets.length,
				verified: tickets.filter((t) => t.ticket.isVerified).length,
				unverified: tickets.filter((t) => !t.ticket.isVerified).length,
				used: tickets.filter((t) => "Used" in t.status).length,
				listed: tickets.filter((t) => "Listed" in t.status).length,
			};
		} catch (error) {
			clientLog.error("Error getting user ticket summary:", error);
			throw error;
		}
	}

	/**
	 * 回收奖券
	 */
	async recycleTicket(
		ticketId: string,
	): Promise<{ recycleAmount: bigint; recycleRate: bigint }> {
		try {
			const result = await this.actor.recycleTicket(ticketId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error recycling ticket:", error);
			throw error;
		}
	}

	/**
	 * 批量回收奖券（前端循环实现）
	 */
	async recycleTickets(ticketIds: string[]): Promise<{
		successCount: number;
		totalAmount: bigint;
		failed: [string, string][];
	}> {
		const failed: [string, string][] = [];
		let successCount = 0;
		let totalAmount = 0n;

		for (const ticketId of ticketIds) {
			try {
				const result = await this.recycleTicket(ticketId);
				successCount++;
				totalAmount += result.recycleAmount;
			} catch (error: any) {
				failed.push([ticketId, error.message || String(error)]);
			}
		}

		return { successCount, totalAmount, failed };
	}

	// ================= 抽奖功能 =================

	/**
	 * 抽奖
	 */
	async drawTicket(ticketId: string): Promise<LuckyWheelTypes.DrawResult> {
		try {
			const result = await this.actor.drawTicket(ticketId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error drawing ticket:", error);
			throw error;
		}
	}

	/**
	 * 批量抽奖
	 */
	async drawTicketsBatch(
		ticketIds: string[],
	): Promise<LuckyWheelTypes.DrawResult[]> {
		try {
			const result = await this.actor.drawTicketsBatch(ticketIds);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error batch drawing tickets:", error);
			throw error;
		}
	}

	/**
	 * 领取奖品
	 */
	async claimPrize(recordId: bigint): Promise<bigint> {
		try {
			const result = await this.actor.claimPrize(recordId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error claiming prize:", error);
			throw error;
		}
	}

	/**
	 * 批量领取奖品（前端循环实现）
	 */
	async claimAllPrizes(
		user: Principal,
	): Promise<Array<{ recordId: bigint; transferId?: bigint; error?: string }>> {
		const results: Array<{
			recordId: bigint;
			transferId?: bigint;
			error?: string;
		}> = [];

		try {
			// 使用v6.0新接口获取所有未领取的奖品
			const result = await this.queryWinRecords(
				{ ByUser: user },
				null,
				{ Forward: null },
				100n,
				{
					claimStatus: [{ Unclaimed: null }],
					prizeLevel: [],
					minAmount: [],
					rarity: [],
				},
			);
			const unclaimedRecords = result.data;

			for (const record of unclaimedRecords) {
				try {
					const transferId = await this.claimPrize(record.id);
					results.push({ recordId: record.id, transferId });
				} catch (error: any) {
					results.push({
						recordId: record.id,
						error: error.message || String(error),
					});
				}
			}

			return results;
		} catch (error) {
			clientLog.error("Error claiming all prizes:", error);
			throw error;
		}
	}

	// ================= 市场交易 =================

	/**
	 * 上架奖券
	 */
	async listTicketForSale(ticketId: string, price: bigint): Promise<void> {
		try {
			const result = await this.actor.listTicketForSale(ticketId, price);
			if ("ok" in result) return;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error listing ticket:", error);
			throw error;
		}
	}

	/**
	 * 取消上架
	 */
	async cancelListing(ticketId: string): Promise<void> {
		try {
			const result = await this.actor.cancelListing(ticketId);
			if ("ok" in result) return;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error canceling listing:", error);
			throw error;
		}
	}

	/**
	 * 从市场购买奖券
	 */
	async buyFromMarket(ticketId: string): Promise<void> {
		try {
			const result = await this.actor.buyFromMarket(ticketId);
			if ("ok" in result) return;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error buying from market:", error);
			throw error;
		}
	}

	/**
	 * 查询活跃市场订单（v6.3.1统一分页接口）
	 *
	 * @param scope - 查询范围：Global（全局）| BySeller（按卖家）| ByRound（按轮次）
	 * @param filter - 可选过滤条件：
	 *   - status: 订单状态（一般为 Active）
	 *   - minPrice/maxPrice: 价格区间（支持单边或双边）
	 *   - minWinProb/maxWinProb: 中奖概率区间 1-100（支持单边或双边）
	 *   - minLevelParam/maxLevelParam: 等级参数区间 1-100（支持单边或双边）
	 * @param cursor - 游标（基于 orderId），首次查询传 null
	 * @param direction - 分页方向：Forward（时间由新到旧）| Backward（时间由旧到新）
	 * @param limit - 每页数量（建议 10-50，最大 100）
	 * @returns 分页结果，包含 data、nextCursor、prevCursor、hasMore、totalCount
	 *
	 * @example
	 * // 查询价格在 1000-5000 之间，中奖概率 ≥50% 的订单
	 * const result = await client.queryActiveOrders(
	 *   { Global: null },
	 *   {
	 *     status: [],
	 *     minPrice: [1000n],
	 *     maxPrice: [5000n],
	 *     minWinProb: [50n],
	 *     maxWinProb: [],
	 *     minLevelParam: [],
	 *     maxLevelParam: [],
	 *   },
	 *   null,
	 *   { Forward: null },
	 *   20n
	 * );
	 */
	async queryActiveOrders(
		scope: LuckyWheelTypes.MarketQueryScope = { Global: null },
		filter: LuckyWheelTypes.MarketFilterV2 | null = null,
		cursor: bigint | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 100n,
	): Promise<LuckyWheelTypes.PaginatedResult_4> {
		try {
			return await this.actor.queryActiveOrders(
				scope,
				filter ? [filter] : [],
				cursor ? [cursor] : [],
				direction,
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying active orders:", error);
			throw error;
		}
	}

	/**
	 * 查询历史市场订单（v6.3.1统一分页接口）
	 *
	 * @param scope - 查询范围：Global（全局）| BySeller（按卖家）| ByRound（按轮次）
	 * @param filter - 可选过滤条件（同 queryActiveOrders）：
	 *   - status: 订单状态（Sold | Cancelled | Expired）
	 *   - minPrice/maxPrice: 价格区间
	 *   - minWinProb/maxWinProb: 中奖概率区间 1-100
	 *   - minLevelParam/maxLevelParam: 等级参数区间 1-100
	 * @param cursor - 游标（基于 orderId），首次查询传 null
	 * @param direction - 分页方向：Forward | Backward
	 * @param limit - 每页数量（建议 10-50，最大 100）
	 * @returns 分页结果，包含 data、nextCursor、prevCursor、hasMore、totalCount
	 */
	async queryOrderHistory(
		scope: LuckyWheelTypes.MarketQueryScope = { Global: null },
		filter: LuckyWheelTypes.MarketFilterV2 | null = null,
		cursor: bigint | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 100n,
	): Promise<LuckyWheelTypes.PaginatedResult_4> {
		try {
			return await this.actor.queryOrderHistory(
				scope,
				filter ? [filter] : [],
				cursor ? [cursor] : [],
				direction,
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying order history:", error);
			throw error;
		}
	}

	/**
	 * 通过orderId获取订单详情（v6.3.1新增）
	 */
	async getOrderById(
		orderId: bigint,
	): Promise<LuckyWheelTypes.MarketOrder | null> {
		try {
			const result = await this.actor.getOrderById(orderId);
			return result.length > 0 ? (result[0] ?? null) : null;
		} catch (error) {
			clientLog.error("Error getting order by id:", error);
			throw error;
		}
	}

	/**
	 * 查询市场挂单（旧接口，保留向后兼容）
	 * @deprecated 建议使用 queryActiveOrders 替代
	 */
	async queryMarketTickets(
		filter?: LuckyWheelTypes.MarketFilter,
		limit: bigint = 100n,
	): Promise<LuckyWheelTypes.MarketOrder[]> {
		try {
			return await this.actor.queryMarketOrders(
				true,
				filter ? [filter] : [],
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying market tickets:", error);
			throw error;
		}
	}

	/**
	 * 获取用户市场挂单（前端过滤实现）
	 */
	async getUserListings(
		user: Principal,
	): Promise<LuckyWheelTypes.MarketOrder[]> {
		try {
			// 使用v6.3.1新接口查询用户的活跃订单
			const result = await this.queryActiveOrders(
				{ BySeller: user },
				null,
				null,
				{ Forward: null },
				1000n,
			);
			return result.data;
		} catch (error) {
			clientLog.error("Error getting user listings:", error);
			throw error;
		}
	}

	// ================= 奖品和轮次 =================

	/**
	 * 获取可用奖品
	 */
	async getAvailablePrizes(): Promise<LuckyWheelTypes.PrizeView[]> {
		try {
			return await this.actor.getAvailablePrizes();
		} catch (error) {
			clientLog.error("Error getting available prizes:", error);
			throw error;
		}
	}

	/**
	 * 获取当前轮次信息（v7.0 更新）
	 */
	async getActiveRoundInfo(): Promise<LuckyWheelTypes.Round | null> {
		try {
			const rounds = await this.actor.getRounds([{ Active: null }]);
			return rounds.length > 0 ? (rounds[0] ?? null) : null;
		} catch (error) {
			clientLog.error("Error getting active round info:", error);
			throw error;
		}
	}

	/**
	 * 获取所有轮次（v7.0 更新 - 支持过滤器）
	 */
	async getRounds(
		filter?: LuckyWheelTypes.RoundQueryFilter,
	): Promise<LuckyWheelTypes.Round[]> {
		try {
			return await this.actor.getRounds(filter ? [filter] : []);
		} catch (error) {
			clientLog.error("Error getting rounds:", error);
			throw error;
		}
	}

	// ================= 统一查询（v6.0+）=================

	/**
	 * 查询操作记录（v6.0统一分页接口）
	 */
	async queryOperations(
		scope: LuckyWheelTypes.OperationQueryScope,
		cursor: bigint | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 20n,
	): Promise<LuckyWheelTypes.PaginatedResult_5> {
		try {
			return await this.actor.queryOperations(
				scope,
				cursor ? [cursor] : [],
				direction,
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying operations:", error);
			throw error;
		}
	}

	/**
	 * 查询中奖记录（v6.0统一分页接口）
	 * v10.1: scope 新增 ByRoundAndUser(roundId, user) 组合查询
	 */
	async queryWinRecords(
		scope: LuckyWheelTypes.WinRecordQueryScope,
		cursor: bigint | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 20n,
		filter: LuckyWheelTypes.WinRecordFilter | null = null,
	): Promise<LuckyWheelTypes.PaginatedResult> {
		try {
			return await this.actor.queryWinRecords(
				scope,
				cursor ? [cursor] : [],
				direction,
				limit,
				filter ? [filter] : [],
			);
		} catch (error) {
			clientLog.error("Error querying win records:", error);
			throw error;
		}
	}

	/**
	 * 按轮次 + 用户组合查询中奖记录（v10.1 辅助方法）
	 */
	async queryWinRecordsByRoundAndUser(
		roundId: bigint,
		user: Principal,
		cursor: bigint | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 20n,
		filter: LuckyWheelTypes.WinRecordFilter | null = null,
	) {
		return this.queryWinRecords(
			{ ByRoundAndUser: { roundId, user } },
			cursor,
			direction,
			limit,
			filter,
		);
	}

	/**
	 * 获取用户完整画像（前端聚合实现）
	 * @deprecated 建议直接使用 getUserBalance + getUserTickets + queryWinRecords 分页查询
	 */
	async getUserProfile(user: Principal): Promise<{
		balance: LuckyWheelTypes.UserBalance;
		tickets: LuckyWheelTypes.TicketInfo[];
		winRecords: LuckyWheelTypes.WinRecord[];
	}> {
		try {
			// 使用v6.0新接口查询中奖记录（降低默认limit，建议使用分页查询）
			const [balance, tickets, winRecordsResult] = await Promise.all([
				this.getUserBalance(user),
				this.getUserTickets(user),
				this.queryWinRecords(
					{ ByUser: user },
					null,
					{ Forward: null },
					20n,
					null,
				),
			]);
			return { balance, tickets, winRecords: winRecordsResult.data };
		} catch (error) {
			clientLog.error("Error getting user profile:", error);
			throw error;
		}
	}

	// ================= 系统信息 =================

	/**
	 * 获取系统统计（v11.0更新：支持轮次级别查询）
	 *
	 * @param scope - 查询范围：
	 *   - Global: 全局统计（所有轮次累计）
	 *   - ActiveRound: 当前活跃轮次统计
	 *   - ByRound(roundId): 指定轮次统计
	 * @returns 系统统计对象
	 *
	 * @example
	 * // 查询全局统计
	 * const globalStats = await client.getSystemStats({ Global: null });
	 *
	 * // 查询当前活跃轮次统计
	 * const activeStats = await client.getSystemStats({ ActiveRound: null });
	 *
	 * // 查询指定轮次统计
	 * const round10Stats = await client.getSystemStats({ ByRound: 10n });
	 */
	async getSystemStats(
		scope: LuckyWheelTypes.StatisticsQueryScope = { Global: null },
	) {
		try {
			return await this.actor.getSystemStats(scope);
		} catch (error) {
			clientLog.error("Error getting system stats:", error);
			throw error;
		}
	}

	/**
	 * 获取当前活跃轮次统计（v11.0便捷方法）
	 */
	async getActiveRoundStats() {
		return this.getSystemStats({ ActiveRound: null });
	}

	/**
	 * 获取全局统计（v11.0便捷方法）
	 */
	async getGlobalStats() {
		return this.getSystemStats({ Global: null });
	}

	/**
	 * 获取指定轮次统计（v11.0便捷方法）
	 */
	async getRoundStats(roundId: bigint) {
		return this.getSystemStats({ ByRound: roundId });
	}

	/**
	 * 获取财务摘要统计（v10.0更新：支持轮次级别查询）
	 *
	 * @param scope - 查询范围：
	 *   - Global: 全局统计（所有轮次累计）
	 *   - ActiveRound: 当前活跃轮次统计
	 *   - ByRound(roundId): 指定轮次统计
	 * @returns 财务摘要对象
	 *
	 * @example
	 * // 查询全局财务统计
	 * const globalStats = await client.getFinancialSummary({ Global: null });
	 *
	 * // 查询当前活跃轮次财务统计
	 * const activeRoundStats = await client.getFinancialSummary({ ActiveRound: null });
	 *
	 * // 查询指定轮次财务统计
	 * const round10Stats = await client.getFinancialSummary({ ByRound: 10n });
	 */
	async getFinancialSummary(
		scope: LuckyWheelTypes.StatisticsQueryScope = { Global: null },
	): Promise<LuckyWheelTypes.FinancialSummary> {
		try {
			return await this.actor.getFinancialSummary(scope);
		} catch (error) {
			clientLog.error("Error getting financial summary:", error);
			throw error;
		}
	}

	/**
	 * 清算轮次（v10.0新增 - 仅管理员）
	 *
	 * @param roundId - 轮次ID
	 * @returns 清算报告
	 */
	async settleRound(
		roundId: bigint,
	): Promise<LuckyWheelTypes.RoundSettlementReport> {
		try {
			const result = await this.actor.settleRound(roundId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error settling round:", error);
			throw error;
		}
	}

	/**
	 * 初始化轮次财务状态（v10.0新增 - 仅管理员）
	 *
	 * @param roundId - 轮次ID
	 */
	async initRoundFinancial(roundId: bigint): Promise<void> {
		try {
			const result = await this.actor.initRoundFinancial(roundId);
			if ("ok" in result) return;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error initializing round financial:", error);
			throw error;
		}
	}

	/**
	 * 获取系统控制状态
	 */
	async getSystemControls(): Promise<LuckyWheelTypes.SystemControls> {
		try {
			return await this.actor.getSystemControls();
		} catch (error) {
			clientLog.error("Error getting system controls:", error);
			throw error;
		}
	}

	/**
	 * 获取所有Token配置（v7.0 更新）
	 */
	async getAllTokenConfigs(): Promise<
		Array<[string, LuckyWheelTypes.TokenConfig]>
	> {
		try {
			return await this.actor.getTokenConfigs([]);
		} catch (error) {
			clientLog.error("Error getting all token configs:", error);
			throw error;
		}
	}

	/**
	 * 获取验证配置（v7.0 更新 - 从统一接口获取）
	 */
	async getVerificationConfig(): Promise<LuckyWheelTypes.VerificationConfig> {
		try {
			const config = await this.actor.getSystemConfig([{ Verification: null }]);
			if (!config.verification || config.verification.length === 0) {
				throw new Error("Verification config not found");
			}
			return config.verification[0];
		} catch (error) {
			clientLog.error("Error getting verification config:", error);
			throw error;
		}
	}

	/**
	 * 获取洗券配置（v7.0 更新 - 从统一接口获取）
	 */
	async getRefreshConfig(): Promise<LuckyWheelTypes.RefreshConfig> {
		try {
			const config = await this.actor.getSystemConfig([{ Refresh: null }]);
			if (!config.refresh || config.refresh.length === 0) {
				throw new Error("Refresh config not found");
			}
			return config.refresh[0];
		} catch (error) {
			clientLog.error("Error getting refresh config:", error);
			throw error;
		}
	}

	/**
	 * 获取系统配置（v7.0 新增统一接口）
	 *
	 * @param type - 可选配置类型，不传则获取所有配置
	 * @returns 系统配置对象，包含：
	 *   - verification: 验证配置
	 *   - refresh: 淬炼配置
	 *   - tokens: 代币配置数组
	 *   - market: 市场配置
	 *   - financial: 财务配置（票价、提现费率）
	 *   - lottery: 抽奖配置
	 *
	 * @example
	 * // 获取所有配置（推荐用于初始化）
	 * const config = await client.getSystemConfig();
	 *
	 * // 获取特定配置
	 * const config = await client.getSystemConfig({ Verification: null });
	 */
	async getSystemConfig(
		type?: LuckyWheelTypes.SystemConfigType,
	): Promise<LuckyWheelTypes.SystemConfigResponse> {
		try {
			return await this.actor.getSystemConfig(type ? [type] : []);
		} catch (error) {
			clientLog.error("Error getting system config:", error);
			throw error;
		}
	}

	// ================= 邀请系统功能 (v8.0) =================

	/**
	 * 获取我的邀请信息
	 * @returns 邀请码、上级邀请码、邀请人数、累计返佣等信息
	 */
	async getMyReferralInfo(): Promise<LuckyWheelTypes.UserReferralInfo> {
		try {
			return await this.actor.getMyReferralInfo();
		} catch (error) {
			clientLog.error("Error getting my referral info:", error);
			throw error;
		}
	}

	/**
	 * 查询邀请信息（支持分页）
	 * @param scope - 查询范围：Global（全局）| ByUser（查询特定用户）| ByReferrer（查询我的下线）
	 * @param cursor - 游标（基于 Text），首次查询传 null
	 * @param direction - 分页方向：Forward（最新绑定在前）| Backward（最旧绑定在前）
	 * @param limit - 每页数量（建议 10-50，最大 100）
	 */
	async queryReferralInfo(
		scope: LuckyWheelTypes.ReferralQueryScope,
		cursor: string | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 20n,
	): Promise<LuckyWheelTypes.PaginatedResult_3> {
		try {
			return await this.actor.queryReferralInfo(
				scope,
				cursor ? [{ Text: cursor }] : [],
				direction,
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying referral info:", error);
			throw error;
		}
	}

	/**
	 * 查询返佣记录（支持分页）
	 * @param scope - 查询范围：Global（全局）| ByReferrer（我的返佣收入）| ByReferee（我给谁带来的返佣）
	 * @param cursor - 游标（基于 Nat），首次查询传 null
	 * @param direction - 分页方向：Forward（最新返佣在前）| Backward（最旧返佣在前）
	 * @param limit - 每页数量（建议 10-50，最大 100）
	 */
	async queryCommissionRecords(
		scope: LuckyWheelTypes.CommissionQueryScope,
		cursor: bigint | null = null,
		direction: LuckyWheelTypes.Direction = { Forward: null },
		limit: bigint = 20n,
	): Promise<LuckyWheelTypes.PaginatedResult_6> {
		try {
			return await this.actor.queryCommissionRecords(
				scope,
				cursor ? [{ Nat: cursor }] : [],
				direction,
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying commission records:", error);
			throw error;
		}
	}

	// ================= 动态奖池功能 (v7.3) =================

	/**
	 * 获取动态奖池状态
	 * @param prizeId - 奖品ID（通常为特等奖ID，level=1）
	 * @returns 动态奖池信息，包含：
	 *   - prize: 奖品详情
	 *   - baseAmount: 基础奖金
	 *   - accumulatedAmount: 当前累积奖池金额
	 *   - currentRewardPerUnit: 当前单份奖励（用户中奖能拿到的总金额）
	 *   - totalContributions: 历史总贡献金额
	 *   - totalDistributed: 已分配出去的金额
	 *   - contributorCount: 贡献次数
	 *
	 * @example
	 * const poolStatus = await client.getDynamicPoolStatus(1n);
	 * clientLog.info(`当前特等奖奖励: ${poolStatus.currentRewardPerUnit} DD`);
	 * clientLog.info(`基础: ${poolStatus.baseAmount}, 动态: +${poolStatus.currentRewardPerUnit - poolStatus.baseAmount}`);
	 */
	async getDynamicPoolStatus(prizeId: bigint): Promise<{
		prize: LuckyWheelTypes.PrizeView;
		baseAmount: bigint;
		accumulatedAmount: bigint;
		currentRewardPerUnit: bigint;
		totalContributions: bigint;
		totalDistributed: bigint;
		contributorCount: bigint;
	}> {
		try {
			const result = await this.actor.getDynamicPoolStatus(prizeId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error getting dynamic pool status:", error);
			throw error;
		}
	}

	/**
	 * 获取奖池贡献历史（验券记录）
	 * @param limit - 返回的记录数量，默认10条
	 * @returns 贡献记录列表，每条记录包含：
	 *   - timestamp: 贡献时间
	 *   - amount: 贡献金额（通常是12豆豆）
	 *   - source: 来源描述（如"验券费"）
	 *   - contributor: 贡献者Principal（可能为空）
	 *
	 * @example
	 * const history = await client.getPoolContributionHistory(10n);
	 * history.forEach(record => {
	 *   clientLog.info(`${new Date(Number(record.timestamp / 1000000n))} - ${record.amount} 豆豆`);
	 * });
	 */
	async getPoolContributionHistory(
		limit: bigint = 10n,
	): Promise<LuckyWheelTypes.PoolContributionRecord[]> {
		try {
			return await this.actor.getPoolContributionHistory(limit);
		} catch (error) {
			clientLog.error("Error getting pool contribution history:", error);
			throw error;
		}
	}

	/**
	 * 获取奖池分配历史（中奖记录）
	 * @param limit - 返回的记录数量，默认10条
	 * @returns 分配记录列表，每条记录包含：
	 *   - timestamp: 中奖时间
	 *   - winner: 中奖者Principal
	 *   - prizeId: 奖品ID
	 *   - prizeName: 奖品名称
	 *   - baseAmount: 基础奖金
	 *   - dynamicAmount: 动态奖金
	 *   - totalAmount: 总金额（基础+动态）
	 *
	 * @example
	 * const history = await client.getPoolDistributionHistory(10n);
	 * history.forEach(record => {
	 *   clientLog.info(`${record.prizeName}: ${record.totalAmount} 豆豆`);
	 *   clientLog.info(`  (基础 ${record.baseAmount} + 动态 ${record.dynamicAmount})`);
	 * });
	 */
	async getPoolDistributionHistory(
		limit: bigint = 10n,
	): Promise<LuckyWheelTypes.PoolDistributionRecord[]> {
		try {
			return await this.actor.getPoolDistributionHistory(limit);
		} catch (error) {
			clientLog.error("Error getting pool distribution history:", error);
			throw error;
		}
	}

	// ================= 积分分红系统 (v12.0) =================

	/**
	 * 兑换单张符券为符力（积分）
	 */
	async exchangeTicketForPoints(
		ticketId: string,
	): Promise<LuckyWheelTypes.PointsExchange> {
		try {
			const result = await this.actor.exchangeTicketsForPoints([ticketId]);
			if ("ok" in result && result.ok.length > 0) {
				return result.ok[0]!;
			}
			throw new Error("err" in result ? result.err : "兑换失败");
		} catch (error) {
			clientLog.error("Error exchanging ticket for points:", error);
			throw error;
		}
	}

	/**
	 * 批量兑换符券为符力
	 */
	async exchangeTicketsForPoints(
		ticketIds: string[],
	): Promise<LuckyWheelTypes.PointsExchange[]> {
		try {
			const result = await this.actor.exchangeTicketsForPoints(ticketIds);
			if ("ok" in result) return result.ok;
			throw new Error("err" in result ? result.err : "批量兑换失败");
		} catch (error) {
			clientLog.error("Error exchanging tickets for points:", error);
			throw error;
		}
	}

	/**
	 * 查询符力排行榜
	 * @param scope - 查询范围：
	 *   - GlobalRanking(topN): 全局TOP N排行榜
	 *   - ByRound(roundId, topN): 指定轮次TOP N排行榜
	 *   - ByUser(user, roundId?): 查询特定用户的符力
	 * @param includeHistory - 是否包含兑换历史明细
	 */
	async queryPoints(
		scope: LuckyWheelTypes.PointsQueryScope,
		includeHistory: boolean = false,
	): Promise<LuckyWheelTypes.PointsQueryResponse> {
		try {
			return await this.actor.queryPoints(scope, includeHistory);
		} catch (error) {
			clientLog.error("Error querying points:", error);
			throw error;
		}
	}

	/**
	 * 获取当前轮次TOP N符力排行榜（便捷方法）
	 * @param topN - 返回前N名，默认10
	 * @param includeHistory - 是否包含兑换历史
	 */
	async getTopPointsRanking(
		topN: bigint = 10n,
		includeHistory: boolean = false,
	) {
		// 先获取当前轮次
		const activeRound = await this.getActiveRoundInfo();
		if (!activeRound) {
			throw new Error("没有活跃的轮次");
		}
		return this.queryPoints(
			{ ByRound: { roundId: activeRound.id, topN: [topN] } },
			includeHistory,
		);
	}

	/**
	 * 获取用户在当前轮次的符力
	 * @param user - 用户Principal
	 */
	async getUserPoints(
		user: Principal,
	): Promise<LuckyWheelTypes.PointsRecordView | null> {
		try {
			const activeRound = await this.getActiveRoundInfo();
			if (!activeRound) return null;

			const result = await this.queryPoints(
				{ ByUser: { user, roundId: [activeRound.id] } },
				false,
			);

			return result.results.length > 0 ? result.results[0]! : null;
		} catch (error) {
			clientLog.error("Error getting user points:", error);
			throw error;
		}
	}

	/**
	 * 获取分红预览（实时计算）
	 * @param roundId - 轮次ID
	 * @returns 分红预览信息，包含：
	 *   - currentTotalPool: 当前总奖池
	 *   - currentBonusPool: 当前奖励池
	 *   - currentBasePool: 当前基础池
	 *   - currentProfit: 当前净利润
	 *   - totalPoints: 总符力
	 *   - participantCount: 参与人数
	 *   - estimatedDividendPerPoint: 每符力预估分红
	 *   - canDividend: 是否可以分红
	 */
	async getDividendPreview(
		roundId: bigint,
	): Promise<LuckyWheelTypes.DividendPreview> {
		try {
			const result = await this.actor.getDividendPreview(roundId);
			if ("ok" in result) return result.ok;
			throw new Error("err" in result ? result.err : "获取分红预览失败");
		} catch (error) {
			clientLog.error("Error getting dividend preview:", error);
			throw error;
		}
	}

	/**
	 * 获取当前轮次分红预览（便捷方法）
	 */
	async getCurrentDividendPreview(): Promise<LuckyWheelTypes.DividendPreview | null> {
		try {
			const activeRound = await this.getActiveRoundInfo();
			if (!activeRound) return null;
			return this.getDividendPreview(activeRound.id);
		} catch (error) {
			clientLog.error("Error getting current dividend preview:", error);
			return null;
		}
	}

	/**
	 * 获取用户历史分红记录
	 * @param user - 可选，不传则查询当前登录用户
	 */
	async getUserDividendHistory(
		user?: Principal,
	): Promise<LuckyWheelTypes.UserDividendHistory> {
		try {
			return await this.actor.getUserDividendHistory(user ? [user] : []);
		} catch (error) {
			clientLog.error("Error getting user dividend history:", error);
			throw error;
		}
	}

	/**
	 * 获取分红结算报告（历史轮次）
	 * @param roundId - 轮次ID
	 * @returns 结算报告，仅在轮次结算后可获取
	 */
	async getDividendReport(
		roundId: bigint,
	): Promise<LuckyWheelTypes.DividendSettlementReport | null> {
		try {
			const result = await this.actor.getDividendReport(roundId);
			return result.length > 0 ? (result[0] ?? null) : null;
		} catch (error) {
			clientLog.error("Error getting dividend report:", error);
			throw error;
		}
	}

	// ================= 工具方法 =================

	/**
	 * 获取 Actor 实例
	 */
	getActor(): _SERVICE {
		return this.actor;
	}

	/**
	 * 获取 Canister ID
	 */
	getCanisterId(): string {
		return this.canisterId;
	}
}
